import { React, useState, useEffect, useRef } from "react";
import axios from "axios";
import * as CONST from "../../../component/config/Constant";
import moment from "moment";
import "../../../assets/css/home.css";
import { FiCalendar, FiGift, FiChevronDown, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { Helmet } from "react-helmet";

function PaidHolidays() {
  const [loading, setLoading] = useState(false);
  const currentYear = new Date().getFullYear();
  const [srchYear, setSrchYear] = useState(currentYear);
  const yearOptions = Array.from({ length: 3 }, (_, index) => currentYear - 1 + index);
  const [holidayData, setholidayData] = useState([]);
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(currentYear);

  const searchYear = (val) => {
    setSrchYear(parseInt(val.target.value, 10));
    setLoading(true);
  };

  useEffect(() => {
    fetchHolidayData();
  }, [srchYear]);

  async function fetchHolidayData() {
    setLoading(true);
    await axios
      .post(CONST.GET_PUBLIC_HOLIDAY_LIST, { year: srchYear })
      .then((response) => {
        const status = response.data.status;
        if (status === "success" && response.data.data) {
          setholidayData(response.data.data);
        } else {
          setholidayData([]);
        }
      })
      .catch(() => {});
    setLoading(false);
  }

  const today = moment();
  const isPast = (dateStr) => moment(dateStr).isBefore(today, "day");
  const isToday = (dateStr) => moment(dateStr).isSame(today, "day");
  const isUpcoming = (dateStr) => moment(dateStr).isAfter(today, "day");
  const isThisMonth = (dateStr) => moment(dateStr).isSame(today, "month") && !isPast(dateStr);
  const nextHoliday = holidayData.find((h) => isUpcoming(h.holiday_date) || isToday(h.holiday_date));
  const nextHolidayRef = useRef(null);

  // Auto-scroll to first upcoming holiday when data loads
  useEffect(() => {
    if (nextHolidayRef.current) {
      setTimeout(() => {
        nextHolidayRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [holidayData]);

  // Calendar helpers
  const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const dayNames = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

  const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const holidayDatesInMonth = holidayData
    .filter((h) => {
      const d = moment(h.holiday_date);
      return d.month() === calMonth && d.year() === calYear;
    })
    .map((h) => ({
      day: moment(h.holiday_date).date(),
      name: h.holiday_name,
      date: h.holiday_date,
    }));

  const prevMonth = () => {
    if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); }
    else setCalMonth(calMonth - 1);
  };

  const nextMonth = () => {
    if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); }
    else setCalMonth(calMonth + 1);
  };

  const daysInMonth = getDaysInMonth(calMonth, calYear);
  const firstDay = getFirstDayOfMonth(calMonth, calYear);
  const calendarDays = [];
  for (let i = 0; i < firstDay; i++) calendarDays.push(null);
  for (let i = 1; i <= daysInMonth; i++) calendarDays.push(i);

  const todayDay = today.date();
  const todayMonth = today.month();
  const todayYear = today.year();

  return (
    <>
      <Helmet>
        <title>{`Paid Holidays ${srchYear} | eLuminous Technologies`}</title>
      </Helmet>

      <section className="holiday-page-full">
        <div className="container-fluid px-0">
          <div className="row g-0 holiday-full-row">

            {/* LEFT: Hero + Calendar */}
            <div className="col-lg-5 holiday-left-panel">
              <div className="holiday-left-inner">
                <span className="hero-badge">Paid Holidays</span>
                <h1 className="holiday-hero-title">
                  Holiday Calendar <span>{srchYear}</span>
                </h1>
                <p className="holiday-hero-subtitle">
                  Plan your year ahead with the complete list of paid holidays
                </p>

                {nextHoliday && (
                  <div className="next-holiday-card">
                    <div className="next-holiday-icon">
                      <FiGift />
                    </div>
                    <div className="next-holiday-info">
                      <span className="next-holiday-label">Next Holiday</span>
                      <strong className="next-holiday-name">{nextHoliday.holiday_name}</strong>
                      <span className="next-holiday-date">
                        {moment(nextHoliday.holiday_date).format("dddd, DD MMM YYYY")}
                      </span>
                    </div>
                  </div>
                )}

                {/* Mini Calendar */}
                <div className="mini-calendar">
                  <div className="mini-cal-header">
                    <button className="mini-cal-nav" onClick={prevMonth}><FiChevronLeft /></button>
                    <span className="mini-cal-month">{monthNames[calMonth]} {calYear}</span>
                    <button className="mini-cal-nav" onClick={nextMonth}><FiChevronRight /></button>
                  </div>
                  <div className="mini-cal-grid">
                    {dayNames.map((d) => (
                      <div className="mini-cal-day-name" key={d}>{d}</div>
                    ))}
                    {calendarDays.map((day, i) => {
                      if (day === null) return <div className="mini-cal-cell empty" key={`e${i}`}></div>;
                      const hol = holidayDatesInMonth.find((h) => h.day === day);
                      const isTodayCell = day === todayDay && calMonth === todayMonth && calYear === todayYear;
                      return (
                        <div
                          className={`mini-cal-cell ${hol ? "mini-cal-holiday" : ""} ${isTodayCell ? "mini-cal-today" : ""}`}
                          key={i}
                          title={hol ? hol.name : ""}
                        >
                          {day}
                          {hol && <span className="mini-cal-dot"></span>}
                        </div>
                      );
                    })}
                  </div>
                  {holidayDatesInMonth.length > 0 && (
                    <div className="mini-cal-legend">
                      {holidayDatesInMonth.map((h, i) => (
                        <div className="mini-cal-legend-item" key={i}>
                          <span className="mini-cal-legend-dot"></span>
                          <span className="mini-cal-legend-date">{moment(h.date).format("DD MMM")}</span>
                          <span className="mini-cal-legend-name">{h.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="holiday-stats mt-4">
                  <div className="holiday-stat-item">
                    <span className="holiday-stat-number holiday-stat-light">{holidayData.length}</span>
                    <span className="holiday-stat-label-light">Total</span>
                  </div>
                  <div className="holiday-stat-item">
                    <span className="holiday-stat-number holiday-stat-light">
                      {holidayData.filter((h) => isUpcoming(h.holiday_date) || isToday(h.holiday_date)).length}
                    </span>
                    <span className="holiday-stat-label-light">Remaining</span>
                  </div>
                  <div className="holiday-stat-item">
                    <span className="holiday-stat-number holiday-stat-light">
                      {holidayData.filter((h) => isPast(h.holiday_date)).length}
                    </span>
                    <span className="holiday-stat-label-light">Passed</span>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT: Full Holiday List */}
            <div className="col-lg-7 holiday-right-panel">
              <div className="holiday-right-inner">
                <div className="holiday-list-header">
                  <h5 className="holiday-list-title">All Holidays</h5>
                  <div className="year-selector">
                    <FiCalendar className="year-selector-icon" />
                    <select value={srchYear} onChange={searchYear} className="year-select">
                      {yearOptions.map((year) => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                    <FiChevronDown className="year-chevron" />
                  </div>
                </div>
                <div className="holiday-full-list">
                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary spinner-border-sm" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : holidayData.length === 0 ? (
                    <div className="text-center py-5 text-muted">No holidays found for {srchYear}</div>
                  ) : (
                    (() => { let firstUpcomingFound = false; return holidayData.map((holiday, index) => {
                      const past = isPast(holiday.holiday_date);
                      const todayFlag = isToday(holiday.holiday_date);
                      const thisMonth = isThisMonth(holiday.holiday_date);
                      const future = isUpcoming(holiday.holiday_date) && !thisMonth;
                      const isFirstUpcoming = !past && !firstUpcomingFound;
                      if (isFirstUpcoming) firstUpcomingFound = true;
                      return (
                        <div
                          ref={isFirstUpcoming ? nextHolidayRef : null}
                          className={`holiday-row ${past ? "holiday-row-past" : ""} ${todayFlag ? "holiday-row-today" : ""} ${thisMonth && !todayFlag ? "holiday-row-thismonth" : ""} ${future ? "holiday-row-future" : ""}`}
                          key={index}
                        >
                          <div className="holiday-row-date">
                            <span className="holiday-row-dd">{moment(holiday.holiday_date).format("DD")}</span>
                            <span className="holiday-row-mmm">{moment(holiday.holiday_date).format("MMM")}</span>
                          </div>
                          <div className="holiday-row-info">
                            <span className="holiday-row-name">{holiday.holiday_name}</span>
                            <span className="holiday-row-day">{holiday.holiday_day}</span>
                          </div>
                          <div className="holiday-row-badge">
                            {todayFlag && <span className="holiday-badge-today">Today</span>}
                            {past && <span className="holiday-badge-past">Passed</span>}
                            {!past && !todayFlag && <span className="holiday-badge-upcoming">Upcoming</span>}
                          </div>
                        </div>
                      );
                    }); })()
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </>
  );
}

export default PaidHolidays;
